import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';

/**
 * Download react node as PDF
 * @param {Element} children node to render as PDF -> required
 * @param {string} name file name -> required
 * @param {function} onClone modify document before generating PDF
 */
const downloadPdf = async (children, name, onClone) => {
  const SharedIntlProviderHOC = (await import('../../../shared-components/hocs/sharedIntlProviderHOC')).default;

  const container = document.getElementById('tax-invoice-container');
  ReactDOM.render(
    <SharedIntlProviderHOC>
      {children}
    </SharedIntlProviderHOC>,
    container
  );
  const pdfPage = document.getElementById('invoice-pdf-page');
  const canvas = await window.html2canvas(pdfPage, {
    windowWidth: 1000,
    scale: 2,
    x: 0,
    allowTaint: true,
    useCORS: true,
    onclone: onClone
  });

  const image = {
    type: 'jpeg',
    quality: 0.98
  };
  const margin = [0.5, 0.5];
  let imgWidth = 8.5;
  let pageHeight = 11;

  let innerPageWidth = imgWidth - margin[0] * 2;
  let innerPageHeight = pageHeight - margin[1] * 2;

  // Calculate the number of pages.
  let pxFullHeight = canvas.height;
  let pxPageHeight = Math.floor(canvas.width * (pageHeight / imgWidth));
  let nPages = Math.ceil(pxFullHeight / pxPageHeight);

  // Define pageHeight separately so it can be trimmed on the final page.
  pageHeight = innerPageHeight;

  // Create a one-page canvas to split up the full image.
  let pageCanvas = document.createElement('canvas');
  let pageCtx = pageCanvas.getContext('2d');
  pageCanvas.width = canvas.width;
  pageCanvas.height = pxPageHeight;

  // Initialize the PDF.
  let doc = new window.jsPDF('p', 'in', [8.5, 11]);
  for (let page = 0; page < nPages; page++) {
    // Trim the final page to reduce file size.
    if (page === nPages - 1 && 0 !== pxFullHeight % pxPageHeight) {
      pageCanvas.height = pxFullHeight % pxPageHeight;
      pageHeight = (pageCanvas.height * innerPageWidth) / pageCanvas.width;
    }

    // Display the page.
    let w = pageCanvas.width;
    let h = pageCanvas.height;
    pageCtx.fillStyle = 'white';
    pageCtx.fillRect(0, 0, w, h);
    pageCtx.drawImage(canvas, 0, page * pxPageHeight, w, h, 0, 0, w, h);

    // Add the page to the PDF.
    if (0 < page) doc.addPage();
    const imgData = pageCanvas.toDataURL('image/' + image.type, image.quality);
    doc.addImage(imgData, image.type, margin[1], margin[0], innerPageWidth, pageHeight);
  }
  doc.save(name);

  ReactDOM.unmountComponentAtNode(container);
};

/**
 * Get tax details
 * @param {string} country user's country -> required
 * @param {object} envWeb web env object -> required
 * @returns {{
 *   taxNumberName: node/null,
 *   taxNumber: string/null,
 *   contactPhoneNumber: string/null,
 * }} tax details and contact info of Peerspace for invoices -> required
 */
const getTaxDetails = (country, envWeb) => {
  if (!envWeb || !country) {
    return {
      taxNumberName: null,
      taxNumber: null
    };
  }

  const TAX_NUMBER_NAME_BY_COUNTRY = {
    US: <FormattedMessage
      id="hosting.sales_tax_invoice.fein"
      defaultMessage="FEIN"
    />,
    AU: <FormattedMessage
      id="hosting.invoice_page.abn"
      defaultMessage="ABN"
    />
  };

  const TAX_NUMBER_ENV_KEY_BY_COUNTRY = {
    US: 'formatted_registration_id',
    AU: 'formatted_abn_id'
  };

  const CONTACT_PHONE_NUMBER_BY_COUNTRY = {
    US: '(415) 403-2421'
  };

  const taxNumberName = TAX_NUMBER_NAME_BY_COUNTRY[country] ?? null;
  const taxNumberEvnKey = TAX_NUMBER_ENV_KEY_BY_COUNTRY[country] ?? null;
  const contactPhoneNumber = CONTACT_PHONE_NUMBER_BY_COUNTRY[country] ?? null;

  const taxNumber = envWeb?.ps_addresses?.[country]?.[taxNumberEvnKey] ?? null;

  return {
    taxNumberName,
    taxNumber,
    contactPhoneNumber
  };
};

export { downloadPdf, getTaxDetails };
